<template>
    <div class="flex flex-col gap-y-2">
        <TruncateTooltip>
            <DynamicLink v-if="
                item.url &&
                (item.type_id == 'web' ||
                    item.type_id == 'social' ||
                    item.type_id == 'feed')
            " :href="item.url">
                <p v-text="item.name" class="underline-hover text-gray-900 font-header text-2xl font-semibold"></p>
            </DynamicLink>

            <Link v-else :href="$localizedRoute(
                'resource.show',
                item.is_linked_resource ? item.resource_id : item.id
            )
                " class="underline-hover text-gray-900 font-header text-2xl font-semibold" v-text="item.name" />
        </TruncateTooltip>

        <div class="flex items-center justify-between gap-x-2">
            <div class="flex items-center gap-x-2">
                <p v-text="$moment(item.created_at).format('L')" :class="['shrink-0 text-sm']" />
                <div class="flex items-center gap-x-2">
                    <TypeTag v-if="item.type" :type="item.type" classes="bg-white block truncate" />

                    <Link v-if="item.is_linked_resource" :href="$localizedRoute('resource.show', item.id)">
                    <PrimaryButton size="xs" class="py-0 text-sm">
                        <p class v-text="$lang.get('Show the link')" />
                    </PrimaryButton>
                    </Link>
                </div>
            </div>

            <ButtonPopoverMenu :items="options" v-if="$isAdmin && showAdminOptions">
                <template #button="{ open }">
                    <div :class="[
                        open
                            ? `border-${resourceableModelClass}-accent bg-${resourceableModelClass}-accent text-${resourceableModelClass}`
                            : 'border-transparent',
                        `border hover:border-${resourceableModelClass}-accent size-10 p-2 rounded-full transition duration-button-hover focus-style`,
                    ]">
                        <PhDotsThreeOutline class="flex-grow" weight="fill" />
                    </div>
                </template>
            </ButtonPopoverMenu>
        </div>
        <div>
            <!-- A youtube player if there is a safeEmbedUrl -->
            <YoutubeEmbed v-if="item.type_id == 'video' && item.safeEmbedUrl" :url="item.safeEmbedUrl"
                class="flex-1 aspect-video" />

            <!-- Feed items (RSS) -->
            <div v-else-if="
                item.type_id == 'feed' && item.feature_id == 'feedfeature'
            ">
                <ResourceListItem :class="['py-1.5']" v-for="(feedItem, index) in item.limitedFeedItems"
                    :key="index + 'feedItem'" :item="feedItem" design="detailed" resourceableModelClass="resource" />
            </div>

            <!-- HTML if an iframe is present in the start of the description -->
            <div v-else-if="displayHtml" v-html="item.description" />

            <!-- ModelImage if there is an image -->
            <ModelImage v-else-if="item.image" :src="item.image" :alt="item.name" :modelClass="item.modelClass"
                :typeId="item.type_id" :useAccentBg="resourceableModelClass == 'resource'"
                fallbackBorder="border border-resource-accent/50" imageClasses="max-w-full aspect-video rounded-lg" />

            <ModelImage v-else-if="hasMedia" :src="currentImage" :alt="item.name" :modelClass="item.modelClass"
                :typeId="item.type_id" :useAccentBg="resourceableModelClass == 'resource'"
                fallbackBorder="border border-resource-accent/50" imageClasses="max-w-full aspect-video rounded-lg" />
            <!-- List -->
            <div v-if="
                item.type_id == 'list' &&
                item.limitedListItems &&
                item.limitedListItems.length
            " class="pb-6 pt-2 flex flex-col gap-y-4">
                <div class="flex flex-col gap-y-4 divide-y divide-border">
                    <ResourceListItem v-for="item in item.limitedListItems" class="pt-4" :key="item.id"
                        :resourceableModelClass="resourceableModelClass" :item="item"
                        :design="item?.data?.design || 'detailed'" />
                </div>
                <Link v-if="item.limitedListItems.length < item.list_items_count" class="mt-5"
                    :href="$localizedRoute(item.modelClass + '.show', item.id)">
                <PrimaryButton class="flex gap-x-2 items-center">
                    <p v-text="$lang.get('See entire list')" />
                    <PhArrowRight class="size-5" />
                </PrimaryButton>
                </Link>
            </div>

            <!-- File -->
            <div v-if="item.type_id == 'file' && item.fileDetails" class="py-4">
                <ResourceFile :file="item.fileDetails" :small="true" />
            </div>

            <!-- Linkbutton if the item is a web or social link -->
            <Link v-if="item.type_id == 'web' || item.type_id == 'social'" :href="$localizedRoute(
                'resource.show',
                item.is_linked_resource ? item.resource_id : item.id
            )
                ">
            <PrimaryButton v-text="$lang.get('Visit resource')" class="my-3" />
            </Link>
        </div>

        <!-- Description -->
        <TruncateTooltip v-if="!displayHtml && (item.short_description || item.description)" maxWidth="max-w-[34rem]">
            <p v-html="item.short_description || item.description" class="line-clamp-2" />
        </TruncateTooltip>

        <!-- Interactions -->
        <ModelInteractions v-if="showModelInteractions" :modelId="item.id" :modelClass="item.modelClass"
            :availableEmotions="availableEmotions" :modelEmotions="item.emotions" :limitedComments="true"
            :initialCommentCount="item.comments_count || 0" :noBorder="true" />
    </div>
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import TruncateTooltip from '@/Components/Utilities/TruncateTooltip.vue'
import YoutubeEmbed from '@/Components/Elements/YoutubeEmbed.vue'
import ModelInteractions from '@/Components/ModelComponents/Interactions/ModelInteractions.vue'
import TypeTag from '@/Components/Elements/Tags/TypeTag.vue'
import { Link } from '@inertiajs/vue3'
import PrimaryButton from '@/Components/Elements/PrimaryButton.vue'
import DynamicLink from '@/Components/Utilities/DynamicLink.vue'
import ResourceListItem from '@/Components/Panels/ResourcePanel/ResourceListItem.vue'
import ResourceFile from '@/Pages/Resource/ShowPageComponents/ResourceFile.vue'
import { PhArrowRight, PhDotsThreeOutline } from '@phosphor-icons/vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'
import BlockedIframeWrapper from '@/Components/ModelComponents/Resources/BlockedIframeWrapper.vue'

export default {
    name: 'ResourceItem',

    components: {
        ModelImage,
        Link,
        TruncateTooltip,
        YoutubeEmbed,
        TypeTag,
        ModelInteractions,
        PrimaryButton,
        DynamicLink,
        ResourceListItem,
        ResourceFile,
        ButtonPopoverMenu,
        PhArrowRight,
        PhDotsThreeOutline,
        BlockedIframeWrapper
    },

    emits: ['deleteModel'],

    props: {
        title: {
            type: String,
            default: 'Related resources',
        },
        item: {
            type: Object,
            default: () => {
                return {
                    image: null,
                    name: null,
                    file: null,
                    created_at: null,
                    type: null,
                    type_id: null,
                    data: {},
                }
            },
            validator: (value) => {
                return (
                    Object.hasOwn(value, 'name') &&
                    Object.hasOwn(value, 'created_at') &&
                    Object.hasOwn(value, 'url') &&
                    Object.hasOwn(value, 'type') &&
                    Object.hasOwn(value, 'type_id')
                )
            },
        },
        // The modelClass where the panel is displayed
        resourceableModelClass: {
            type: String,
            required: true,
        },

        availableEmotions: {
            type: Array,
            required: true,
        },
        showModelInteractions: {
            type: Boolean,
            default: true,
        },
        showAdminOptions: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            isHovering: false,
        }
    },

    created() {
        if (this.$isAdmin && this.showAdminOptions) {
            this.options = [
                {
                    text: this.$lang.get('Edit'),
                    href: this.$localizedRoute(
                        `${this.item.modelClass}.edit`,
                        this.item.id
                    ),
                    type: 'link',
                },
                {
                    text: this.$lang.get('Delete'),
                    type: 'button',
                    action: () => {
                        this.$emit('deleteModel', this.item)
                    },
                },
            ]
        }
    },

    computed: {
        /**
         * Only display html if a summary was not given and the type is html and if the descriptions has an iframe tag within the first 30 characters
         */
        displayHtml() {
            return this.item.type_id == 'html' &&
                this.item.short_description == null //&&
            // this.item.description.slice(0, 30).includes('<iframe')
            // )
        },

        currentImage() {
            return route('media.show', { media: this.item.mediaIds[0] })
        },

        hasMedia() {
            return Object.hasOwn(this.item, 'mediaIds') && this.item.mediaIds.length > 0
        },
    },
}
</script>
